import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  MapPin, Star, Users, Trees, Ruler, BedDouble, Bath, Car, Waves, Sailboat,
  Wifi, Zap, Speaker, Accessibility, LayoutGrid, Utensils, Music, AlertTriangle,
} from 'lucide-react';
import { TID } from '@yalidavet/shared';
import Gallery from '@/components/mansion/Gallery';
import BookingWidget from '@/components/booking/BookingWidget';
import { api } from '@/lib/api';

const AMENITY_ICONS: Record<string, any> = {
  hasPool: Waves, hasGarden: Trees, hasSeaView: Sailboat, hasParking: Car,
  hasWifi: Wifi, hasGenerator: Zap, hasSoundSystem: Speaker, isAccessible: Accessibility,
  hasStagingArea: LayoutGrid, hasCateringArea: Utensils, hasMusicLicence: Music,
};
const AMENITY_LABELS: Record<string, string> = {
  hasPool: 'Havuzlu', hasGarden: 'Bahçeli', hasSeaView: 'Deniz Manzaralı', hasParking: 'Otoparklı',
  hasWifi: 'Wi-Fi', hasGenerator: 'Jeneratör', hasSoundSystem: 'Ses Sistemi', isAccessible: 'Engelli Erişimi',
  hasStagingArea: 'Sahne / Kurulum Alanı', hasCateringArea: 'Catering Alanı', hasMusicLicence: 'Müzik İzni',
};

export default function PropertyDetailPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const guestCount = params.get('guestCount') ? Number(params.get('guestCount')) : undefined;

  const { data: mansion, isLoading, isError } = useQuery({
    queryKey: ['mansion', slug, guestCount],
    queryFn: () => api.mansion(slug!, { guestCount, eventTypeId: params.get('eventTypeId') ?? undefined }),
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="yd-container space-y-4 py-8">
        <div className="yd-skeleton h-8 w-1/2" />
        <div className="yd-skeleton h-96 w-full" />
      </div>
    );
  }

  if (isError || !mansion) {
    return (
      <div className="yd-container py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Mülk bulunamadı</h1>
        <Link to="/ara" className="yd-btn-primary mt-4">Aramaya dön</Link>
      </div>
    );
  }

  const activeAmenities = Object.keys(AMENITY_LABELS).filter((k) => (mansion as any)[k] === true);

  return (
    <div className="yd-container py-6">
      {/* Header */}
      <header className="mb-4">
        <h1 className="font-display text-3xl font-bold text-ink-900">{mansion.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-ink-600">
          <span className="flex items-center gap-1">
            <Star className="h-4 w-4 fill-sand-500 text-sand-500" aria-hidden />
            <strong>{mansion.ratingAvg.toFixed(1)}</strong> ({mansion.ratingCount} değerlendirme)
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="h-4 w-4" aria-hidden />
            {mansion.neighborhood ? `${mansion.neighborhood}, ` : ''}{mansion.district}, {mansion.city}
          </span>
        </div>
      </header>

      <Gallery photos={mansion.photos} title={mansion.title} />

      <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_380px]">
        {/* ------------------- Left column ------------------- */}
        <div className="space-y-10">
          {/* Key stats */}
          <section className="grid grid-cols-2 gap-4 border-b border-ink-100 pb-8 sm:grid-cols-4">
            {[
              { icon: Users, label: 'Maks. kapasite', value: `${mansion.maxCapacity} kişi` },
              { icon: Trees, label: 'Bahçe alanı', value: `${mansion.gardenSizeM2} m²` },
              { icon: Ruler, label: 'Toplam alan', value: `${mansion.totalAreaM2} m²` },
              { icon: BedDouble, label: 'Yatak odası', value: `${mansion.bedrooms}` },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center gap-2.5">
                <Icon className="h-5 w-5 shrink-0 text-bosphorus-700" aria-hidden />
                <div>
                  <p className="text-xs text-ink-500">{label}</p>
                  <p className="font-semibold text-ink-900">{value}</p>
                </div>
              </div>
            ))}
          </section>

          {/* Mülk Hakkında */}
          <section data-testid={TID.aboutSection}>
            <h2 className="font-display text-xl font-bold text-ink-900">Mülk Hakkında</h2>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-ink-700">
              {mansion.description || 'Bu mülk için henüz açıklama eklenmemiş.'}
            </p>

            <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid={TID.amenityList}>
              {activeAmenities.map((key) => {
                const Icon = AMENITY_ICONS[key] ?? Trees;
                return (
                  <li key={key} className="flex items-center gap-2 text-sm text-ink-700" data-amenity={key}>
                    <Icon className="h-4 w-4 text-bosphorus-700" aria-hidden />
                    {AMENITY_LABELS[key]}
                  </li>
                );
              })}
              {mansion.hasParking && (
                <li className="flex items-center gap-2 text-sm text-ink-700">
                  <Car className="h-4 w-4 text-bosphorus-700" aria-hidden />
                  {mansion.parkingCapacity} araçlık otopark
                </li>
              )}
              <li className="flex items-center gap-2 text-sm text-ink-700">
                <Bath className="h-4 w-4 text-bosphorus-700" aria-hidden />
                {mansion.bathrooms} banyo
              </li>
            </ul>
          </section>

          {/* Uygun Organizasyonlar */}
          <section data-testid={TID.eventTypesSection}>
            <h2 className="font-display text-xl font-bold text-ink-900">Uygun Organizasyonlar</h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {mansion.approvedEventTypes.length > 0 ? (
                mansion.approvedEventTypes.map((e) => (
                  <li key={e.id} className="yd-pill" data-event-type={e.id}>{e.labelTr}</li>
                ))
              ) : (
                <li className="text-sm text-ink-500">Henüz onaylı organizasyon tipi yok.</li>
              )}
            </ul>

            {/* Wedding auto-flags */}
            {(mansion.hasStagingArea || mansion.hasCateringArea) && (
              <p className="mt-3 text-sm text-ink-600" data-testid="property-wedding-flags">
                {mansion.hasStagingArea && <>Sahne/kurulum alanı: <strong>{mansion.stagingAreaM2} m²</strong>. </>}
                {mansion.hasCateringArea && <>Catering alanı: <strong>{mansion.cateringAreaM2} m²</strong>.</>}
              </p>
            )}

            {/* Host rule warning box */}
            <div
              className="mt-5 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4"
              data-testid={TID.hostRuleWarning}
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">Ev sahibi kuralları</p>
                <p className="mt-1 whitespace-pre-line">
                  {mansion.houseRules ??
                    'Mülke giriş saat ' + mansion.checkinTime + ', kesin çıkış saat ' + mansion.checkoutDeadline + "'dır."}
                </p>
                {mansion.isResidentialZone && (
                  <p className="mt-2">
                    Bu mülk <strong>yerleşim bölgesindedir</strong>. Müzik yayını en geç{' '}
                    <strong>{mansion.noiseCurfewTime}</strong>, mülkten çıkış en geç{' '}
                    <strong>{mansion.checkoutDeadline}</strong> olmalıdır. Aksi halde depozitodan kesinti yapılır.
                  </p>
                )}
                {mansion.neighborhoodNotes && <p className="mt-2">{mansion.neighborhoodNotes}</p>}
              </div>
            </div>
          </section>

          {/* Host */}
          <section className="flex items-center gap-4 border-t border-ink-100 pt-8">
            {mansion.host.avatarUrl ? (
              <img src={mansion.host.avatarUrl} alt="" className="h-14 w-14 rounded-full object-cover" />
            ) : (
              <div className="grid h-14 w-14 place-items-center rounded-full bg-bosphorus-100 font-semibold text-bosphorus-800">
                {mansion.host.fullName.slice(0, 1)}
              </div>
            )}
            <div>
              <p className="text-sm text-ink-500">Ev sahibi</p>
              <p className="font-semibold text-ink-900">{mansion.host.fullName}</p>
            </div>
          </section>
        </div>

        {/* ------------------- Booking widget ------------------- */}
        <BookingWidget
          mansion={mansion}
          initial={{
            checkin: params.get('checkin') ?? undefined,
            checkout: params.get('checkout') ?? undefined,
            guestCount,
            eventTypeId: params.get('eventTypeId') ?? undefined,
          }}
        />
      </div>
    </div>
  );
}
